import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { createInvoice, getInvoice, getNextNumber, parseExcel, updateInvoice, listAdjustments } from "../lib/api";
import { formatEUR, MONTHS_ES, parseNumber } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Plus, Trash2, ArrowLeft, Save, FileCheck2, Upload, Sparkles, Info, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import InvoiceTypeSwitcher from "../components/InvoiceTypeSwitcher";
import ServicesEditor, { DEFAULT_SERVICES } from "../components/ServicesEditor";
import DuplicateNamesDialog from "../components/DuplicateNamesDialog";
import MissingAdelantoDialog from "../components/MissingAdelantoDialog";
import { findDuplicateNames, findTripsMissingAdelanto } from "../lib/clientNames";
import { listInvoices } from "../lib/api";

const monthLabel = (key) => {
  if (!key) return "Sin fecha";
  const [y, m] = key.split("-");
  const idx = Math.max(0, Math.min(11, Number(m) - 1));
  return `${MONTHS_ES[idx]} ${y}`;
};

/**
 * Convert a trip's USD values to EUR using its tipo_cambio (or fallback rate).
 * Rule (verified from historical data): EUR = USD / TC
 * Adelanto defaults to 40% of Ingresado € (editable).
 * Final billing per client = (Gastado € − Adelanto €) + Shared Profit (50% of beneficio).
 */
const computeEur = (trip, fallbackRate) => {
  const rate = Number(trip.tipo_cambio) > 0 ? Number(trip.tipo_cambio) : Number(fallbackRate || 0);
  let gastado_eur = Number(trip.gastado_eur || 0);
  let ingresado_eur = Number(trip.ingresado_eur || 0);
  if (!gastado_eur && trip.gastado_usd && rate > 0) gastado_eur = trip.gastado_usd / rate;
  if (!ingresado_eur && trip.ingresado_usd && rate > 0) ingresado_eur = trip.ingresado_usd / rate;
  // Adelanto: use stored value if present, otherwise 40% of ingresado
  const adelanto_eur = trip.adelanto_eur != null && trip.adelanto_eur !== ""
    ? Number(trip.adelanto_eur)
    : Number((ingresado_eur * 0.4).toFixed(2));
  const profit_share = Math.max(0, (ingresado_eur - gastado_eur) / 2);
  const final_eur = Math.max(0, gastado_eur - adelanto_eur);
  return {
    gastado_eur: Number(gastado_eur.toFixed(2)),
    ingresado_eur: Number(ingresado_eur.toFixed(2)),
    adelanto_eur: Number(adelanto_eur.toFixed(2)),
    profit_share: Number(profit_share.toFixed(2)),
    final_eur: Number(final_eur.toFixed(2)),
    effective_rate: rate,
  };
};

export default function FinalInvoiceForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const nav = useNavigate();
  const today = new Date();
  const fileRef = useRef(null);

  const [number, setNumber] = useState("");
  const [issueDate, setIssueDate] = useState(today.toISOString().slice(0, 10));
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());
  const [notes, setNotes] = useState("");
  // Pending adjustments from Adjustments module — shown as a banner so the
  // user can drop them into "Extras" with one click.
  const [pendingAdjustments, setPendingAdjustments] = useState({ balance: 0, count: 0, items: [] });
  const [adjustmentApplied, setAdjustmentApplied] = useState(false);
  const [status, setStatus] = useState("draft");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [specialists, setSpecialists] = useState(9);
  const [includeServices, setIncludeServices] = useState(false);
  const [services, setServices] = useState(DEFAULT_SERVICES);

  const [trips, setTrips] = useState([]);
  const [fallbackRate, setFallbackRate] = useState(0); // default exchange rate for rows missing it
  const [rateDialogOpen, setRateDialogOpen] = useState(false);
  const [rateInput, setRateInput] = useState("");

  const [tripLines, setTripLines] = useState([]);
  const [extras, setExtras] = useState([]);

  useEffect(() => {
    (async () => {
      if (isEdit) {
        try {
          const inv = await getInvoice(id);
          setNumber(inv.number);
          setIssueDate(inv.issue_date);
          setMonth(inv.period_month);
          setYear(inv.period_year);
          setTripLines(inv.lines.filter((l) => l.group === "trips"));
          setExtras(inv.lines.filter((l) => l.group === "extras"));
          const existingServices = inv.lines.filter((l) => l.group === "services");
          if (existingServices.length > 0) {
            setServices(existingServices.map((l, i) => ({ id: l.id || `loaded-${i}`, description: l.description, amount: l.amount, enabled: true })));
            setIncludeServices(true);
          } else {
            setIncludeServices(false);
          }
          setSpecialists(inv.specialists_count || 9);
          if (inv.trips && inv.trips.length > 0) {
            setTrips(inv.trips.map((t, i) => ({ id: t.id || `loaded-trip-${i}`, ...t })));
          }
          if (inv.default_exchange_rate) setFallbackRate(inv.default_exchange_rate);
          setNotes(inv.notes || "");
          setStatus(inv.status);
        } catch {
          toast.error("Error cargando factura");
        }
      } else {
        try {
          const n = await getNextNumber(year);
          setNumber(n.number);
        } catch {
          // Non-critical: number will fall back to user input
        }
      }
    })();
    // Load pending adjustments (independent of edit/create mode)
    (async () => {
      try {
        const d = await listAdjustments(true);
        setPendingAdjustments({
          balance: d.pending_balance || 0,
          count: d.pending_count || 0,
          items: d.adjustments || [],
        });
      } catch { /* non-blocking */ }
    })();
    // eslint-disable-next-line
  }, [id]);

  // Derived trips with EUR values computed
  const tripsWithEur = useMemo(
    () => trips.map((t) => ({ ...t, ...computeEur(t, fallbackRate) })),
    [trips, fallbackRate]
  );

  const tripsByMonth = useMemo(() => {
    const map = {};
    tripsWithEur.forEach((t) => {
      const key = t.arrival_month || "sin-fecha";
      if (!map[key]) map[key] = [];
      map[key].push(t);
    });
    return map;
  }, [tripsWithEur]);

  const subtotal = useMemo(
    () => [...tripLines, ...extras].reduce((a, l) => a + Number(l.amount || 0), 0),
    [tripLines, extras]
  );

  const servicesLines = useMemo(
    () => services.filter((s) => s.enabled).map((s) => ({
      id: s.id,
      description: s.description,
      amount: Number(s.amount || 0),
      group: "services",
    })),
    [services]
  );

  const servicesSum = includeServices ? servicesLines.reduce((a, l) => a + l.amount, 0) : 0;
  const grandTotal = subtotal + servicesSum;

  const handleExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await parseExcel(file);
      if (res.error) {
        toast.error(res.error);
        return;
      }
      if (!res.trips || res.trips.length === 0) {
        toast.warning("No se detectaron viajes en el Excel");
        return;
      }
      const newTrips = res.trips.map((t, i) => ({
        id: `excel-${Date.now()}-${i}`,
        ...t,
      }));
      setTrips([...trips, ...newTrips]);
      if (res.excluded_count && res.excluded_count > 0) {
        const names = (res.excluded || []).slice(0, 5).map((x) => x.name).join(", ");
        const more = res.excluded_count > 5 ? ` y ${res.excluded_count - 5} más` : "";
        toast.warning(
          `${newTrips.length} viajes importados. ${res.excluded_count} EXCLUIDOS por ya facturados: ${names}${more}`,
          { duration: 12000 }
        );
      } else {
        toast.success(`${newTrips.length} viajes importados`);
      }

      // If any row needs an exchange rate → ask
      if (res.needs_rate) {
        setRateInput("");
        setRateDialogOpen(true);
      }
    } catch (err) {
      toast.error("Error procesando Excel: " + err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const applyFallbackRate = () => {
    const r = parseNumber(rateInput);
    if (!r || r <= 0) {
      toast.error("Introduce un tipo de cambio válido");
      return;
    }
    setFallbackRate(r);
    setRateDialogOpen(false);
    toast.success(`Tipo de cambio aplicado: 1 € = ${r} $`);
  };

  const generateLinesFromTrips = () => {
    if (tripsWithEur.length === 0) {
      toast.error("No hay viajes para procesar");
      return;
    }
    // Check still missing EUR data
    const missing = tripsWithEur.filter(
      (t) => (t.has_usd_data && (!t.gastado_eur && !t.ingresado_eur))
    );
    if (missing.length > 0) {
      toast.error(`${missing.length} viajes sin tipo de cambio. Indícalo en la columna TC o pulsa "Tipo de cambio".`);
      return;
    }

    const groups = Object.entries(tripsByMonth).sort(([a], [b]) => a.localeCompare(b));
    const newLines = [];
    groups.forEach(([key, items]) => {
      const monthName = monthLabel(key);
      const totalFinal = items.reduce((a, t) => a + Number(t.final_eur || 0), 0);
      const totalShared = items.reduce((a, t) => a + Number(t.profit_share || 0), 0);
      const names = items.map((t) => t.client_name).join(", ");

      newLines.push({
        id: `gen-${key}-hdr-${Date.now()}`,
        description: `Travel Services Booked for ${monthName} (Final Payment + Shared Profit)`,
        amount: 0,
        group: "trips",
        is_header: true,
      });
      newLines.push({
        id: `gen-${key}-trip-${Date.now()}`,
        description: `Trip bookings final payment: ${names}`,
        amount: Number(totalFinal.toFixed(2)),
        group: "trips",
        is_header: false,
      });
      if (totalShared > 0) {
        newLines.push({
          id: `gen-${key}-profit-${Date.now()}`,
          description: `Shared Profit Services`,
          amount: Number(totalShared.toFixed(2)),
          group: "trips",
          is_header: false,
        });
      }
    });
    setTripLines([...tripLines.filter((l) => !String(l.id || "").startsWith("gen-")), ...newLines]);
    toast.success(`${newLines.length} líneas generadas desde ${tripsWithEur.length} viajes`);
  };

  const updateTripRow = (idx, field, v) => {
    const arr = [...trips];
    const numericFields = ["gastado_usd", "gastado_eur", "ingresado_usd", "ingresado_eur", "tipo_cambio", "adelanto_eur"];
    arr[idx] = { ...arr[idx], [field]: numericFields.includes(field) ? parseNumber(v) : v };
    setTrips(arr);
  };
  const removeTripRow = (idx) => setTrips(trips.filter((_, i) => i !== idx));
  const addTripRow = () => setTrips([
    ...trips,
    {
      id: `manual-${Date.now()}`,
      client_name: "",
      arrival_date: "",
      arrival_month: `${year}-${String(month).padStart(2, "0")}`,
      gastado_usd: 0,
      gastado_eur: 0,
      ingresado_usd: 0,
      ingresado_eur: 0,
      tipo_cambio: 0,
      adelanto_eur: 0,
      has_eur_data: true,
      has_usd_data: false,
    },
  ]);

  const addLine = () => setTripLines([
    ...tripLines,
    { id: `tmp-${Date.now()}`, description: "", amount: 0, group: "trips" },
  ]);
  const updateLine = (i, field, v) => {
    const arr = [...tripLines];
    arr[i] = { ...arr[i], [field]: field === "amount" ? parseNumber(v) : v };
    setTripLines(arr);
  };
  const removeLine = (i) => setTripLines(tripLines.filter((_, idx) => idx !== i));

  const addExtra = () => setExtras([...extras, { id: `tmp-${Date.now()}`, description: "", amount: 0, group: "extras" }]);

  // Apply all pending adjustments as a single "Pricing Discrepancies" line
  // and stage the apply: the backend only marks them applied when the invoice
  // is saved (with its real id) — we trigger that from handleSave below.
  const applyPendingAdjustments = () => {
    if (!pendingAdjustments.count) return;
    const dates = pendingAdjustments.items
      .map((a) => a.source_date).filter(Boolean).sort();
    const desc = dates.length === 1
      ? `Pricing Discrepancies (ajustes ${dates[0]})`
      : dates.length
        ? `Pricing Discrepancies (ajustes ${dates[0]} a ${dates[dates.length-1]})`
        : "Pricing Discrepancies";
    setExtras([...extras, {
      id: `adj-${Date.now()}`,
      description: desc,
      amount: pendingAdjustments.balance,
      group: "extras",
      _from_adjustments: true,
    }]);
    setAdjustmentApplied(true);
    toast.success(`Ajuste de ${pendingAdjustments.balance.toFixed(2)} € añadido. Se marcarán como aplicados al guardar.`);
  };
  const updateExtra = (i, field, v) => {
    const arr = [...extras];
    arr[i] = { ...arr[i], [field]: field === "amount" ? parseNumber(v) : v };
    setExtras(arr);
  };
  const removeExtra = (i) => setExtras(extras.filter((_, idx) => idx !== i));

  const [duplicates, setDuplicates] = useState([]);
  const [duplicatesDialogOpen, setDuplicatesDialogOpen] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);
  const [missingAdelanto, setMissingAdelanto] = useState([]);
  const [missingAdelantoDialogOpen, setMissingAdelantoDialogOpen] = useState(false);

  const performSave = async (payload) => {
    try {
      let savedId;
      let savedNumber;
      if (isEdit) {
        await updateInvoice(id, payload);
        savedId = id;
        savedNumber = payload.number;
        toast.success("Factura actualizada");
      } else {
        const inv = await createInvoice(payload);
        savedId = inv.id;
        savedNumber = inv.number;
        toast.success(`Factura ${inv.number} creada`);
      }
      // Adjustments now liquidate automatically on the backend when an issued
      // Final invoice contains a "Pricing Discrepancies" line — no separate
      // call needed.
      if (adjustmentApplied && payload.status === "issued") {
        toast.success(`Ajustes pendientes marcados como aplicados en ${savedNumber}`);
      }
      nav(`/factura/${savedId}`);
    } catch (e) {
      toast.error("Error guardando factura: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const save = async (finalize = false) => {
    setSaving(true);
    const allLines = [
      ...(includeServices ? servicesLines.map((l) => ({ description: l.description, amount: l.amount, group: "services", is_header: false })) : []),
      ...tripLines.map((l) => ({ description: l.description, amount: Number(l.amount), group: "trips", is_header: Boolean(l.is_header) })),
      ...extras.map((l) => ({ description: l.description, amount: Number(l.amount), group: "extras", is_header: Boolean(l.is_header) })),
    ];
    const payload = {
      type: "final",
      issue_date: issueDate,
      period_month: Number(month),
      period_year: Number(year),
      specialists_count: Number(specialists),
      lines: allLines,
      trips: tripsWithEur,
      default_exchange_rate: Number(fallbackRate || 0),
      vat_rate: 0,
      notes,
      number,
      status: finalize ? "issued" : status,
    };

    // When emitting, check for duplicate client names in previous final invoices
    if (finalize) {
      try {
        const allInvoices = await listInvoices();
        const dups = findDuplicateNames(payload.lines, allInvoices, "final", id || null);
        if (dups.length > 0) {
          setDuplicates(dups);
          setPendingPayload(payload);
          setDuplicatesDialogOpen(true);
          setSaving(false);
          return;
        }
      } catch {
        // Non-critical: if the duplicate-check call fails the invoice
        // proceeds without the warning prompt (best-effort UX).
      }
    }

    await performSave(payload);
  };

  const totalGastado = tripsWithEur.reduce((a, t) => a + Number(t.gastado_eur || 0), 0);
  const totalIngresado = tripsWithEur.reduce((a, t) => a + Number(t.ingresado_eur || 0), 0);
  const totalAdelanto = tripsWithEur.reduce((a, t) => a + Number(t.adelanto_eur || 0), 0);
  const totalFinal = tripsWithEur.reduce((a, t) => a + Number(t.final_eur || 0), 0);
  const totalShared = tripsWithEur.reduce((a, t) => a + Number(t.profit_share || 0), 0);
  const rowsMissingRate = tripsWithEur.filter((t) => t.has_usd_data && !t.gastado_eur && !t.ingresado_eur).length;

  return (
    <div data-testid="final-form-page" className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <button onClick={() => nav("/")} className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="back-btn">
            <ArrowLeft size={14} /> Volver
          </button>
          <h1 className="font-cabinet text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900 mt-2">
            {isEdit ? "Editar Factura Final" : "Nueva Factura Final"}
          </h1>
          <p className="text-slate-500 mt-1 text-sm">100% del gastado + 50% del beneficio (Ingresado − Gastado)</p>
        </div>
        <div className="text-right space-y-2">
          <div>
            <div className="text-xs uppercase tracking-[0.16em] text-slate-500">Nº Factura</div>
            <div className="font-cabinet text-2xl font-bold text-slate-900">{number || "—"}</div>
          </div>
          {isEdit && <InvoiceTypeSwitcher invoiceId={id} currentType="final" />}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Period */}
          <section className="bg-white border border-slate-200 rounded-lg p-6">
            <h2 className="font-cabinet text-lg font-semibold text-slate-900 mb-4">Datos generales</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-xs font-medium text-slate-600">Mes</Label>
                <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
                  <SelectTrigger data-testid="month-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MONTHS_ES.map((m, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Año</Label>
                <Input type="number" data-testid="year-input" value={year} onChange={(e) => setYear(Number(e.target.value))} className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Fecha emisión</Label>
                <Input type="date" data-testid="issue-date-input" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Nº Factura</Label>
                <Input data-testid="number-input" value={number} onChange={(e) => setNumber(e.target.value)} className="mt-1.5" />
              </div>
            </div>
          </section>

          {/* Services (optional) */}
          <section className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  role="switch"
                  aria-checked={includeServices}
                  data-testid="include-services-toggle"
                  onClick={() => setIncludeServices(!includeServices)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${includeServices ? "bg-slate-900" : "bg-slate-300"}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${includeServices ? "translate-x-4" : "translate-x-1"}`} />
                </button>
                <h2 className="font-cabinet text-lg font-semibold text-slate-900">Servicios fijos</h2>
              </div>
              <p className="text-xs text-slate-500">Edita textos, importes o desactiva líneas individualmente</p>
            </div>
            {includeServices ? (
              <ServicesEditor services={services} setServices={setServices} />
            ) : (
              <p className="text-xs text-slate-500">Los servicios fijos no aparecerán en esta factura. Activa el interruptor para incluirlos.</p>
            )}
          </section>

          {/* Formula explainer */}
          <div className="bg-blue-50/60 border border-blue-100 rounded-lg p-4 text-sm text-blue-900 flex gap-3">
            <Info size={16} className="mt-0.5 flex-shrink-0" />
            <div>
              <strong>Cómo se calcula:</strong> Por cada cliente <code className="bg-blue-100 px-1 rounded">Final = Gastado − Adelanto</code> y <code className="bg-blue-100 px-1 rounded">Profit 50% = (Ingresado − Gastado) / 2</code>. El <strong>Adelanto se lee directamente de la columna "Adelanto" del Excel</strong> (lo ya facturado); si la celda viene vacía, por defecto = 40% × Ingresado. Agrupado por mes de llegada. Conversión: <code className="bg-blue-100 px-1 rounded">EUR = USD / Tipo de Cambio</code>.
            </div>
          </div>

          {/* Excel upload */}
          <section className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="font-cabinet text-lg font-semibold text-slate-900">Importar Excel de viajes cerrados</h2>
                <p className="text-xs text-slate-500 mt-1">Detecta automáticamente: <span className="font-mono">Gastado ($/€)</span>, <span className="font-mono">Ingresado ($/€)</span>, <span className="font-mono">Tipo de Cambio</span>.</p>
              </div>
            </div>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleExcel} className="hidden" data-testid="excel-file-input" />
            <Button
              variant="outline"
              data-testid="upload-excel-btn"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="w-full justify-center border-dashed border-slate-300 py-6"
            >
              <Upload size={16} className="mr-2" />
              {uploading ? "Procesando..." : "Subir archivo .xlsx o .csv"}
            </Button>

            {fallbackRate > 0 && (
              <div className="mt-4 flex items-center justify-between gap-3 px-4 py-3 bg-slate-50 border border-slate-200 rounded-md">
                <div className="text-sm">
                  <span className="text-slate-600">Tipo de cambio por defecto:</span>{" "}
                  <span className="font-mono font-semibold text-slate-900">1 € = {fallbackRate.toFixed(4)} $</span>
                </div>
                <Button size="sm" variant="ghost" onClick={() => { setRateInput(String(fallbackRate)); setRateDialogOpen(true); }} data-testid="change-rate-btn">
                  Cambiar
                </Button>
              </div>
            )}

            {rowsMissingRate > 0 && (
              <div className="mt-4 flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-900">
                <AlertTriangle size={16} />
                <span>
                  <strong>{rowsMissingRate} viaje(s)</strong> sin tipo de cambio. Rellena la columna TC o{" "}
                  <button className="underline font-semibold" onClick={() => setRateDialogOpen(true)} data-testid="open-rate-prompt">
                    introduce un tipo por defecto
                  </button>.
                </span>
              </div>
            )}
          </section>

          {/* Trips table */}
          <section className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-cabinet text-lg font-semibold text-slate-900">Viajes ({trips.length})</h2>
                <p className="text-xs text-slate-500 mt-0.5">Editable. Si introduces solo $, se convertirá a € con el TC.</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={addTripRow} data-testid="add-trip-btn">
                  <Plus size={14} className="mr-1" /> Añadir
                </Button>
                <Button
                  size="sm"
                  className="bg-slate-900 hover:bg-slate-800"
                  onClick={generateLinesFromTrips}
                  disabled={trips.length === 0}
                  data-testid="generate-lines-btn"
                >
                  <Sparkles size={14} className="mr-1" /> Generar líneas
                </Button>
              </div>
            </div>

            {trips.length === 0 ? (
              <div className="text-sm text-slate-400 py-10 text-center border border-dashed border-slate-200 rounded-md">
                Sube un Excel o añade viajes manualmente
              </div>
            ) : (
              <div className="overflow-x-auto -mx-6 px-6">
                <Table className="min-w-[1050px]">
                  <TableHeader>
                    <TableRow className="bg-slate-50 hover:bg-slate-50 border-slate-200">
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-900">Cliente</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-900 w-28">Llegada</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-900 w-20 text-right">Gast $</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-900 w-20 text-right">Ingr $</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-900 w-16 text-right">TC</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-900 w-20 text-right">Gast €</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-900 w-20 text-right">Ingr €</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-amber-800 bg-amber-50/50 w-20 text-right" title="Importado del Excel">Adelanto</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-emerald-800 bg-emerald-50/50 w-20 text-right" title="Gastado € − Adelanto €">Final €</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-900 w-20 text-right">50% Profit</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tripsWithEur.map((t, i) => {
                      const computedFromUsd = !trips[i].gastado_eur && t.gastado_usd > 0;
                      return (
                        <TableRow key={t.id} data-testid={`trip-row-${i}`} className="border-slate-100">
                          <TableCell className="py-1.5">
                            <Input value={t.client_name} onChange={(e) => updateTripRow(i, "client_name", e.target.value)} className="h-8" data-testid={`trip-name-${i}`} />
                          </TableCell>
                          <TableCell className="py-1.5">
                            <Input
                              type="date"
                              value={t.arrival_date || ""}
                              onChange={(e) => {
                                updateTripRow(i, "arrival_date", e.target.value);
                                if (e.target.value) updateTripRow(i, "arrival_month", e.target.value.slice(0, 7));
                              }}
                              className="h-8 text-xs"
                            />
                          </TableCell>
                          <TableCell className="py-1.5">
                            <Input type="number" step="0.01" value={trips[i].gastado_usd} onChange={(e) => updateTripRow(i, "gastado_usd", e.target.value)} className="h-8 text-right font-mono text-xs" />
                          </TableCell>
                          <TableCell className="py-1.5">
                            <Input type="number" step="0.01" value={trips[i].ingresado_usd} onChange={(e) => updateTripRow(i, "ingresado_usd", e.target.value)} className="h-8 text-right font-mono text-xs" />
                          </TableCell>
                          <TableCell className="py-1.5">
                            <Input
                              type="number"
                              step="0.0001"
                              value={trips[i].tipo_cambio || ""}
                              placeholder={fallbackRate ? fallbackRate.toFixed(4) : ""}
                              onChange={(e) => updateTripRow(i, "tipo_cambio", e.target.value)}
                              className="h-8 text-right font-mono text-xs"
                            />
                          </TableCell>
                          <TableCell className="py-1.5">
                            <Input
                              type="number"
                              step="0.01"
                              value={trips[i].gastado_eur || (computedFromUsd ? t.gastado_eur : 0)}
                              onChange={(e) => updateTripRow(i, "gastado_eur", e.target.value)}
                              className={`h-8 text-right font-mono text-xs ${computedFromUsd ? "bg-slate-50 text-slate-700" : ""}`}
                              title={computedFromUsd ? "Calculado desde $ / TC" : ""}
                            />
                          </TableCell>
                          <TableCell className="py-1.5">
                            <Input
                              type="number"
                              step="0.01"
                              value={trips[i].ingresado_eur || (computedFromUsd ? t.ingresado_eur : 0)}
                              onChange={(e) => updateTripRow(i, "ingresado_eur", e.target.value)}
                              className={`h-8 text-right font-mono text-xs ${computedFromUsd ? "bg-slate-50 text-slate-700" : ""}`}
                            />
                          </TableCell>
                          <TableCell className="py-1.5 bg-amber-50/30">
                            <Input
                              type="number"
                              step="0.01"
                              value={t.adelanto_eur}
                              onChange={(e) => updateTripRow(i, "adelanto_eur", e.target.value)}
                              className="h-8 text-right font-mono text-xs"
                              title="Adelanto importado del Excel (lo que ya se cobró). Editable."
                            />
                          </TableCell>
                          <TableCell className="text-right font-mono tabular-nums text-xs text-emerald-900 bg-emerald-50/30 py-1.5 font-semibold">
                            {formatEUR(t.final_eur || 0)}
                          </TableCell>
                          <TableCell className="text-right font-mono tabular-nums text-xs text-slate-700 py-1.5">
                            {formatEUR(t.profit_share || 0)}
                          </TableCell>
                          <TableCell className="py-1.5">
                            <Button size="icon" variant="ghost" onClick={() => removeTripRow(i)} data-testid={`trip-remove-${i}`}>
                              <Trash2 size={14} className="text-rose-600" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow className="bg-slate-50 border-t-2 border-slate-200 font-semibold">
                      <TableCell colSpan={5} className="text-slate-900 text-xs uppercase tracking-wider">Totales</TableCell>
                      <TableCell className="text-right font-mono tabular-nums text-slate-900 text-xs">{formatEUR(totalGastado)}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums text-slate-900 text-xs">{formatEUR(totalIngresado)}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums text-amber-900 text-xs bg-amber-50/30">{formatEUR(totalAdelanto)}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums text-emerald-900 text-xs bg-emerald-50/30">{formatEUR(totalFinal)}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums text-slate-900 text-xs">{formatEUR(totalShared)}</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            )}
          </section>

          {/* Generated invoice lines */}
          <section className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-cabinet text-lg font-semibold text-slate-900">Líneas de factura</h2>
                <p className="text-xs text-slate-500 mt-0.5">Las cabeceras (en gris) se muestran centradas, sin importe.</p>
              </div>
              <Button size="sm" variant="outline" onClick={addLine} data-testid="add-line-btn">
                <Plus size={14} className="mr-1" /> Añadir línea
              </Button>
            </div>
            {tripLines.length === 0 ? (
              <div className="text-sm text-slate-400 py-6 text-center border border-dashed border-slate-200 rounded-md">
                Pulsa "Generar líneas" arriba para crearlas desde los viajes
              </div>
            ) : (
              <div className="space-y-2">
                {tripLines.map((l, i) => (
                  <div key={l.id || i} className={`flex gap-2 items-start p-2 rounded-md ${l.is_header ? "bg-slate-50 border border-slate-200" : ""}`} data-testid={`line-row-${i}`}>
                    <button
                      type="button"
                      onClick={() => {
                        const arr = [...tripLines];
                        arr[i] = { ...arr[i], is_header: !arr[i].is_header, amount: !arr[i].is_header ? 0 : arr[i].amount };
                        setTripLines(arr);
                      }}
                      className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded ${l.is_header ? "bg-slate-900 text-white" : "bg-white border border-slate-300 text-slate-600 hover:bg-slate-100"}`}
                      title={l.is_header ? "Cabecera de bloque (clic para convertir en línea)" : "Línea normal (clic para convertir en cabecera)"}
                      data-testid={`line-${i}-toggle-header`}
                    >
                      {l.is_header ? "Cabecera" : "Línea"}
                    </button>
                    <Textarea
                      value={l.description}
                      onChange={(e) => updateLine(i, "description", e.target.value)}
                      className={`flex-1 min-h-[40px] ${l.is_header ? "font-semibold text-center" : ""}`}
                      rows={2}
                    />
                    {!l.is_header && (
                      <Input type="number" step="0.01" value={l.amount} onChange={(e) => updateLine(i, "amount", e.target.value)} className="w-32 text-right font-mono" />
                    )}
                    {l.is_header && <div className="w-32" />}
                    <Button size="icon" variant="ghost" onClick={() => removeLine(i)}>
                      <Trash2 size={14} className="text-rose-600" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Extras */}
          <section className="bg-white border border-slate-200 rounded-lg p-6">
            {pendingAdjustments.count > 0 && !adjustmentApplied && (
              <div className="mb-4 p-3 rounded-md bg-amber-50 border border-amber-200 flex items-center justify-between gap-3">
                <div className="text-xs">
                  <strong>{pendingAdjustments.count}</strong> {pendingAdjustments.count === 1 ? "ajuste pendiente" : "ajustes pendientes"}
                  {" · "}
                  <span className={pendingAdjustments.balance >= 0 ? "text-amber-800" : "text-sky-800"}>
                    <strong>
                      {pendingAdjustments.balance >= 0 ? "+" : "−"}
                      {Math.abs(pendingAdjustments.balance).toLocaleString("es-ES", { style: "currency", currency: "EUR", minimumFractionDigits: 2 })}
                    </strong>
                  </span>
                  {" "}{pendingAdjustments.balance >= 0 ? "a favor de VdV" : "a favor de ET"}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={applyPendingAdjustments}
                  className="border-amber-400 text-amber-900 hover:bg-amber-100"
                  data-testid="apply-pending-adjustments-btn"
                >
                  Aplicar como Pricing Discrepancies
                </Button>
              </div>
            )}
            {adjustmentApplied && (
              <div className="mb-4 p-3 rounded-md bg-emerald-50 border border-emerald-200 text-xs text-emerald-900">
                ✓ Ajustes pendientes incluidos abajo. Se marcarán como aplicados al guardar la factura.
              </div>
            )}
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-cabinet text-lg font-semibold text-slate-900">Ajustes / Extras</h2>
                <p className="text-xs text-slate-500 mt-0.5">Pricing discrepancies u otros ajustes</p>
              </div>
              <Button size="sm" variant="outline" onClick={addExtra} data-testid="add-extras-btn">
                <Plus size={14} className="mr-1" /> Añadir
              </Button>
            </div>
            {extras.length === 0 ? (
              <div className="text-sm text-slate-400 py-6 text-center border border-dashed border-slate-200 rounded-md">
                Sin ajustes
              </div>
            ) : (
              <div className="space-y-2">
                {extras.map((l, i) => (
                  <div key={l.id || i} className="flex gap-2 items-start" data-testid={`extras-row-${i}`}>
                    <Input value={l.description} onChange={(e) => updateExtra(i, "description", e.target.value)} className="flex-1" />
                    <Input type="number" step="0.01" value={l.amount} onChange={(e) => updateExtra(i, "amount", e.target.value)} className="w-32 text-right font-mono" />
                    <Button size="icon" variant="ghost" onClick={() => removeExtra(i)}>
                      <Trash2 size={14} className="text-rose-600" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="bg-white border border-slate-200 rounded-lg p-6">
            <Label className="text-xs font-medium text-slate-600">Notas (opcional)</Label>
            <Textarea data-testid="notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5" rows={3} />
          </section>
        </div>

        {/* Summary */}
        <aside className="lg:sticky lg:top-8 self-start space-y-4">
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="text-xs uppercase tracking-[0.16em] text-slate-500">Resumen factura</div>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-600">Servicios fijos</span><span className="font-mono tabular-nums">{includeServices ? formatEUR(servicesSum) : "—"}</span></div>
              <div className="flex justify-between"><span className="text-slate-600">Líneas viajes ({tripLines.length})</span><span className="font-mono tabular-nums">{formatEUR(tripLines.reduce((a, l) => a + Number(l.amount || 0), 0))}</span></div>
              <div className="flex justify-between"><span className="text-slate-600">Ajustes ({extras.length})</span><span className="font-mono tabular-nums">{formatEUR(extras.reduce((a, l) => a + Number(l.amount || 0), 0))}</span></div>
            </div>
            <div className="border-t border-slate-200 mt-4 pt-4 flex items-baseline justify-between">
              <span className="text-sm text-slate-600">Total</span>
              <span data-testid="summary-total" className="font-cabinet text-2xl font-bold text-slate-900 tabular-nums">{formatEUR(grandTotal)}</span>
            </div>
            <div className="text-xs text-slate-400 mt-1">IVA 0%</div>
          </div>

          {trips.length > 0 && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
              <div className="text-xs uppercase tracking-[0.16em] text-slate-500">Cálculo viajes</div>
              <div className="mt-3 space-y-1.5 text-xs">
                <div className="flex justify-between"><span className="text-slate-600">Σ Gastado €</span><span className="font-mono">{formatEUR(totalGastado)}</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Σ Ingresado €</span><span className="font-mono">{formatEUR(totalIngresado)}</span></div>
                <div className="flex justify-between text-amber-900"><span>− Σ Adelanto (del Excel)</span><span className="font-mono">{formatEUR(totalAdelanto)}</span></div>
                <div className="flex justify-between text-emerald-900 font-medium pt-1.5 border-t border-slate-200"><span>= Σ Final (Gast − Adel.)</span><span className="font-mono">{formatEUR(totalFinal)}</span></div>
                <div className="flex justify-between text-slate-900 font-medium pt-1.5 border-t border-slate-200"><span>+ Σ Shared Profit (50%)</span><span className="font-mono">{formatEUR(totalShared)}</span></div>
                <div className="flex justify-between text-slate-900 font-semibold pt-1.5 border-t border-slate-200"><span>A facturar</span><span className="font-mono">{formatEUR(totalFinal + totalShared)}</span></div>
              </div>
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-3">
            <Button data-testid="save-draft-btn" variant="outline" className="w-full justify-center" onClick={() => save(false)} disabled={saving}>
              <Save size={14} className="mr-2" /> Guardar borrador
            </Button>
            <Button data-testid="save-issued-btn" className="w-full justify-center bg-slate-900 hover:bg-slate-800" onClick={() => save(true)} disabled={saving}>
              <FileCheck2 size={14} className="mr-2" /> Emitir factura
            </Button>
          </div>
        </aside>
      </div>

      {/* Exchange rate dialog */}
      <Dialog open={rateDialogOpen} onOpenChange={setRateDialogOpen}>
        <DialogContent data-testid="rate-dialog">
          <DialogHeader>
            <DialogTitle>Tipo de cambio</DialogTitle>
            <DialogDescription>
              El Excel no incluye tipo de cambio para algunas filas. Introduce el tipo de cambio EUR → USD que se aplicará a las filas que estén solo en dólares.
              <br />Fórmula: <code className="bg-slate-100 px-1 py-0.5 rounded text-xs">EUR = USD / Tipo de Cambio</code>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label className="text-xs font-medium text-slate-600">1 € = ___ $</Label>
            <Input
              type="number"
              step="0.0001"
              placeholder="Ej: 1.08"
              value={rateInput}
              onChange={(e) => setRateInput(e.target.value)}
              autoFocus
              data-testid="rate-input"
            />
            <p className="text-xs text-slate-500">Lo podrás cambiar después o sobreescribirlo en cada fila.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRateDialogOpen(false)} data-testid="rate-cancel-btn">Cancelar</Button>
            <Button onClick={applyFallbackRate} className="bg-slate-900 hover:bg-slate-800" data-testid="rate-apply-btn">Aplicar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DuplicateNamesDialog
        open={duplicatesDialogOpen}
        onOpenChange={(o) => { setDuplicatesDialogOpen(o); if (!o) { setSaving(false); setPendingPayload(null); } }}
        duplicates={duplicates}
        invoiceType="final"
        onConfirm={async () => {
          setDuplicatesDialogOpen(false);
          if (pendingPayload) {
            setSaving(true);
            await performSave(pendingPayload);
            setPendingPayload(null);
          }
        }}
      />

      <MissingAdelantoDialog
        open={missingAdelantoDialogOpen}
        onOpenChange={(o) => { setMissingAdelantoDialogOpen(o); if (!o) { setSaving(false); setPendingPayload(null); } }}
        warnings={missingAdelanto}
        onConfirm={async () => {
          setMissingAdelantoDialogOpen(false);
          // After confirming missing adelanto, continue with duplicate name check
          if (pendingPayload) {
            try {
              const allInvoices = await listInvoices();
              const dups = findDuplicateNames(pendingPayload.lines, allInvoices, "final", id || null);
              if (dups.length > 0) {
                setDuplicates(dups);
                setDuplicatesDialogOpen(true);
                return;
              }
            } catch {
              // Non-critical: skip duplicate-name check on failure
            }
            setSaving(true);
            await performSave(pendingPayload);
            setPendingPayload(null);
          }
        }}
      />
    </div>
  );
}
